import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Plus, PencilSimple, UserMinus, ArrowClockwise, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import TeamMemberDialog from "@/components/TeamMemberDialog";

/*
  Team & Capacity page.

  Iter 85.64 · Team Utility — the roster is now a first-class admin
  surface for the firm. Partners can Add/Edit/Deactivate/Reactivate any
  member; every authenticated user can edit their OWN name + capacity
  via the "Edit my info" shortcut in the header. Historical attribution
  on notes/tasks/engagements is preserved because those records store
  `user.id` (not names) — deactivation is a soft-delete that keeps the
  user document in place with `active: false`.
*/
export default function Team() {
  const { user: currentUser } = useAuth();
  const isPartner = (() => {
    const r = (currentUser?.role || "").toLowerCase();
    // Iter 85.65 · Managing Principal shares Partner-tier authority for
    // Team administration (add / edit / deactivate). Matches the
    // server-side `_PARTNER_TIER_ROLES` set in `routers/auth.py`.
    return r === "partner" || r === "managing principal";
  })();

  const [rows, setRows] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    const q = showInactive ? "?include_inactive=1" : "";
    api.get(`/team/capacity${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(load, [showInactive]);

  const { active, inactive } = useMemo(() => {
    const a = [], i = [];
    for (const r of rows) (r.user?.active === false ? i : a).push(r);
    return { active: a, inactive: i };
  }, [rows]);

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (u) => { setEditing(u); setDialogOpen(true); };
  const openSelfEdit = () => { setEditing(currentUser); setDialogOpen(true); };

  const deactivate = async (u) => {
    if (!confirm(`Deactivate ${u.name}? Their historical notes/tasks stay intact.`)) return;
    try {
      await api.post(`/users/${u.id}/deactivate`);
      toast.success(`${u.name} deactivated`);
      // Auto-reveal the inactive section so the row doesn't seem to vanish.
      if (!showInactive) setShowInactive(true); else load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deactivate failed");
    }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reactivate failed");
    }
  };

  return (
    <PageShell size="default">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">People</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Team &amp; Capacity</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Utilization across active engagements. Estimated hours from open tasks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={openSelfEdit}
            data-testid="team-edit-my-info"
            className="rounded-none border-border"
          >
            <UserCircle size={14} weight="bold" className="mr-1.5" /> Edit my info
          </Button>
          {isPartner && (
            <Button
              onClick={openAdd}
              data-testid="team-add-member"
              className="rounded-none bg-primary text-primary-foreground"
            >
              <Plus size={14} weight="bold" className="mr-1.5" /> Add member
            </Button>
          )}
        </div>
      </div>

      {isPartner && (
        <div className="mb-4 flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-primary"
              data-testid="team-show-inactive-toggle"
            />
            Show deactivated
          </label>
        </div>
      )}

      <RosterTable
        rows={active}
        isPartner={isPartner}
        currentUserId={currentUser?.id}
        onEdit={openEdit}
        onDeactivate={deactivate}
        emptyLabel="No team members yet."
      />

      {showInactive && inactive.length > 0 && (
        <div className="mt-8">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Deactivated · {inactive.length}
          </div>
          <RosterTable
            rows={inactive}
            isPartner={isPartner}
            currentUserId={currentUser?.id}
            onEdit={openEdit}
            onReactivate={reactivate}
            deactivated
            emptyLabel="No deactivated members."
          />
        </div>
      )}

      <TeamMemberDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        member={editing}
        currentUser={currentUser}
        onSaved={load}
      />
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Roster table — the same layout for both active + inactive sections so
// deactivated members still show utilization + engagement history for
// audit. Row-level actions vary by section (Deactivate vs. Reactivate).
// ─────────────────────────────────────────────────────────────────────────
function RosterTable({ rows, isPartner, currentUserId, onEdit, onDeactivate, onReactivate, deactivated, emptyLabel }) {
  return (
    <div className={`border border-border bg-card ${deactivated ? "opacity-70" : ""}`}>
      <table className="w-full text-sm">
        <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          <tr className="border-b border-border">
            <th className="text-left px-5 py-3 font-normal">Member</th>
            <th className="text-left px-3 py-3 font-normal">Role</th>
            <th className="text-left px-3 py-3 font-normal w-1/3">Utilization</th>
            <th className="text-right px-3 py-3 font-normal">Allocated</th>
            <th className="text-right px-3 py-3 font-normal">Capacity</th>
            <th className="text-left px-5 py-3 font-normal">Active engagements</th>
            <th className="text-right px-5 py-3 font-normal w-24"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan="7" className="px-5 py-12 text-center text-xs text-muted-foreground">{emptyLabel}</td></tr>
          )}
          {rows.map((r) => {
            const isSelf = r.user.id === currentUserId;
            const canEdit = isPartner || isSelf;
            return (
              <tr
                key={r.user.id}
                className="border-b border-border/50 hover:bg-secondary/30 group"
                data-testid={`team-row-${r.user.id}`}
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border border-border bg-secondary flex items-center justify-center font-mono text-xs text-primary">
                      {r.user.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium">{r.user.name}{isSelf && <span className="ml-2 text-[0.625rem] font-mono uppercase tracking-widest text-primary">You</span>}</div>
                      <div className="text-[0.6875rem] text-muted-foreground">{r.user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest">{r.user.role}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-secondary overflow-hidden">
                      <div className="h-full" style={{
                        width: Math.min(r.utilization_pct, 150) + "%",
                        background: r.utilization_pct > 100
                          ? "hsl(var(--destructive))"
                          : r.utilization_pct > 85
                            ? "hsl(var(--brass))"
                            : "hsl(var(--chart-2))",
                      }} />
                    </div>
                    <span className="text-xs font-mono w-10 text-right data-cell">{r.utilization_pct}%</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-right data-cell">{r.allocated_hours}h</td>
                <td className="px-3 py-3 text-right data-cell text-muted-foreground">{r.capacity_hours}h</td>
                <td className="px-5 py-3">
                  {r.engagements.length === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
                    <div className="flex flex-wrap gap-1.5">
                      {r.engagements.slice(0, 4).map((e) => (
                        <Link key={e.id} to={`/engagements/${e.id}`} className="text-[0.625rem] font-mono uppercase tracking-widest border border-border px-2 py-0.5 hover:border-primary hover:text-primary">
                          {e.name}
                        </Link>
                      ))}
                      {r.engagements.length > 4 && <span className="text-[0.625rem] text-muted-foreground">+{r.engagements.length - 4}</span>}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && (
                      <button
                        onClick={() => onEdit(r.user)}
                        title={isSelf ? "Edit your info" : `Edit ${r.user.name}`}
                        data-testid={`team-edit-${r.user.id}`}
                        className="p-1 text-muted-foreground hover:text-primary"
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                    )}
                    {isPartner && !deactivated && !isSelf && (
                      <button
                        onClick={() => onDeactivate(r.user)}
                        title={`Deactivate ${r.user.name}`}
                        data-testid={`team-deactivate-${r.user.id}`}
                        className="p-1 text-muted-foreground hover:text-destructive"
                      >
                        <UserMinus size={13} weight="bold" />
                      </button>
                    )}
                    {isPartner && deactivated && (
                      <button
                        onClick={() => onReactivate(r.user)}
                        title={`Reactivate ${r.user.name}`}
                        data-testid={`team-reactivate-${r.user.id}`}
                        className="p-1 text-muted-foreground hover:text-primary"
                      >
                        <ArrowClockwise size={13} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
